import { useState, useEffect, useRef, useId } from "react";
import { Play, Pause, Volume2 } from "lucide-react";
import pulseMarkImg from "@assets/Pulse_1780355676299.png";

// ─── Animated Pulse Mark ─────────────────────────────────────────────────────
// Shows the static Pulse logo mark (PNG) with a glowing light that travels along
// an SVG path tracing the yellow waveform centerline. The mark stays still; only
// the traveling glow animates over it. Respects prefers-reduced-motion.
//
// Shared by the Daily Pulse feature page, the /features index featured card, and
// the home-page Pulse block so the animation lives in exactly one place.
export function PulseWaveformMark({ className = "" }: { className?: string }) {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Unique SVG ids so multiple marks can render on the same page without
  // clashing filter/gradient/path references.
  const uid = useId().replace(/:/g, "");
  const glowId = `pulse-glow-${uid}`;
  const gradId = `pulse-grad-${uid}`;
  const pathId = `pulse-path-${uid}`;

  // Exact path traced from the yellow waveform centerline of the 375×375 Pulse
  // mark (attached_assets/Pulse_1780355676299.png) — extracted by sampling the
  // per-column centroid of the brand-yellow pixels, then simplified
  // (Ramer–Douglas–Peucker). It follows the mark's true contour: left flat tail
  // → small dip → tall peak → deep valley → secondary peak → right flat tail.
  //
  // The traced anchor points are converted from straight L segments into a
  // smooth Catmull–Rom spline (rendered as cubic C Bezier commands) so the
  // traveling glow eases organically through the peak/valley turns instead of
  // snapping direction. The curve still passes through every original anchor,
  // hugging the same centerline.
  const motionPath =
    "M85,214.5 C91.5,214.5 116,213.75 124,214.5 C132,215.25 129.5,216.33 133,219 " +
    "C136.5,221.67 142.5,228.83 145,230.5 C147.5,232.17 146,232.08 148,229 " +
    "C150,225.92 153.5,221.25 157,212 C160.5,202.75 165.5,183.17 169,173.5 " +
    "C172.5,163.83 175,154.42 178,154 C181,153.58 183,159.75 187,171 " +
    "C191,182.25 198,210.08 202,221.5 C206,232.92 209,236.5 211,239.5 " +
    "C213,242.5 213,240.25 214,239.5 C215,238.75 214.5,240.08 217,235 " +
    "C219.5,229.92 225.5,215.33 229,209 C232.5,202.67 232.5,196.08 238,197 " +
    "C243.5,197.92 254.5,211.58 262,214.5 C269.5,217.42 279.5,214.5 283,214.5";

  return (
    <div
      className={`relative ${className}`}
      aria-hidden="true"
      data-testid="pulse-waveform-mark"
    >
      {/* Ambient glow behind the mark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[70%] h-[70%] rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      {/* Static Pulse mark (PNG) — always visible */}
      <img
        src={pulseMarkImg}
        alt=""
        className="relative w-full h-auto select-none"
        style={{ filter: "drop-shadow(0 0 24px rgba(59,130,246,0.35))" }}
        draggable={false}
      />

      {/* Animated SVG overlay — glow travels along the traced waveform */}
      {!prefersReducedMotion && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 375 375"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <filter id={glowId} x="-200%" y="-200%" width="500%" height="500%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="40%" stopColor="#60a5fa" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Hidden path for animateMotion */}
          <path id={pathId} d={motionPath} fill="none" stroke="none" />

          {/* Outer glow disk — hugs the stroke, gently pulsing */}
          <circle cx="0" cy="0" r="13" fill={`url(#${gradId})`} opacity="0.6" filter={`url(#${glowId})`}>
            <animate attributeName="r" values="11;15;11" dur="1.1s" repeatCount="indefinite" />
            <animateMotion dur="4s" repeatCount="indefinite" rotate="none">
              <mpath href={`#${pathId}`} />
            </animateMotion>
          </circle>

          {/* Mid glow */}
          <circle cx="0" cy="0" r="6.5" fill="#60a5fa" opacity="0.9" filter={`url(#${glowId})`}>
            <animateMotion dur="4s" repeatCount="indefinite" rotate="none">
              <mpath href={`#${pathId}`} />
            </animateMotion>
          </circle>

          {/* Bright core */}
          <circle cx="0" cy="0" r="3.5" fill="white" opacity="1">
            <animateMotion dur="4s" repeatCount="indefinite" rotate="none">
              <mpath href={`#${pathId}`} />
            </animateMotion>
          </circle>
        </svg>
      )}
    </div>
  );
}

// ─── Voice Player ────────────────────────────────────────────────────────────
// The pulsating "Hear an example" play button that streams the example briefing.
export function PulseExamplePlayer({ size = "default" }: { size?: "default" | "large" }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    };
  }, []);

  function handleToggle() {
    if (error) {
      setError(false);
    }

    const existingAudio = audioRef.current;

    if (existingAudio && playing) {
      existingAudio.pause();
      setPlaying(false);
      return;
    }

    if (!existingAudio || existingAudio.src === "") {
      setLoading(true);
      const audio = new Audio("/pulse-example.mp3");
      audioRef.current = audio;

      audio.oncanplaythrough = () => {
        setLoading(false);
      };

      audio.onended = () => {
        setPlaying(false);
      };

      audio.onerror = () => {
        setPlaying(false);
        setLoading(false);
        setError(true);
      };

      audio
        .play()
        .then(() => {
          setLoading(false);
          setPlaying(true);
        })
        .catch(() => {
          setPlaying(false);
          setLoading(false);
          setError(true);
        });
      return;
    }

    // Resume paused audio
    existingAudio
      .play()
      .then(() => setPlaying(true))
      .catch(() => setError(true));
  }

  const isLarge = size === "large";

  return (
    <div className="flex flex-col items-center gap-3" data-testid="pulse-voice-player">
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`group relative flex items-center justify-center gap-3 rounded-full font-bold transition-all duration-300 ${
          isLarge ? "px-9 py-5 text-lg" : "px-7 py-4 text-base"
        } ${
          playing
            ? "bg-blue-600 text-white shadow-xl shadow-blue-500/50 scale-[1.02]"
            : "bg-blue-500 hover:bg-blue-400 text-white shadow-xl shadow-blue-500/40 hover:shadow-blue-400/60 hover:scale-[1.04]"
        } disabled:opacity-60 disabled:cursor-wait`}
        data-testid="button-pulse-example-play"
        aria-label={playing ? "Pause example briefing" : "Play example briefing"}
      >
        {/* Attention ring — pings when idle, steady pulse while playing */}
        <span
          className={`absolute -inset-1.5 rounded-full border-2 pointer-events-none ${
            playing
              ? "border-blue-300/60 animate-pulse"
              : "border-blue-400/70 animate-ping"
          }`}
        />

        <span
          className={`relative flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/30 transition-colors ${
            isLarge ? "w-11 h-11" : "w-9 h-9"
          }`}
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          ) : playing ? (
            <Pause size={isLarge ? 20 : 18} className="text-white" />
          ) : (
            <Play size={isLarge ? 20 : 18} className="text-white ml-0.5" />
          )}
        </span>

        <span className="relative leading-tight">
          {loading ? "Loading…" : playing ? "Playing — tap to pause" : "Hear an example"}
        </span>

        <Volume2 size={isLarge ? 20 : 18} className="relative ml-1 text-white/80" />
      </button>

      {error && (
        <p className="text-xs text-red-400/80" data-testid="text-pulse-player-error">
          Couldn't load the audio. Please try again.
        </p>
      )}

      {playing && (
        <p className="text-xs text-blue-300/60 animate-pulse" data-testid="text-pulse-playing-indicator">
          ● Playing your Daily Pulse example…
        </p>
      )}
    </div>
  );
}
